import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { createUUID } from '../../../utils/createUUID';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { BannerModel } from '../../../context/models/banners/BannerModel';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { AppState } from '../../../context/stores/rootReducers';
import { actions } from '../../../context/stores/rootActions';
import { BannerActionTypes } from '../../../context/stores/banner/IBannerActionsTypes';
import { no_img } from '../../../components/shared/hoc-static';
import { history } from '../../../utils/history';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button as ShadcnButton } from '../../../components/ui/button';
import { Edit } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';

type IBannerTableProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allBanners: BannerModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<BannerModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleGetDetail: (id: string) => void;
	handleSaveData: (data: BannerModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<BannerModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Banner`,
		status: state.banner.status,
		allBanners: state.banner.allBanners,
		selectedIds: state.banner.selectedIds,
		showDetail: state.banner.showDetail,
		showConfirm: state.banner.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<BannerActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.banner.getAllRequest());
	},
	handleSaveData: (data: BannerModel) => {
		dispatch(actions.banner.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.banner.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.banner.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<BannerModel>) => {
		dispatch(actions.banner.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.banner.showConfirm(isShow));
	},
});

const BannerTable = (props: IBannerTableProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		allBanners,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		onShowConfirm,
	} = props;

	const handleAddEditAction = (id?: string) => {
		history.replace(location.pathname);
		navigate(`${location.pathname}/edit/${id || '0'}`, { replace: false });
	};

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<BannerModel>[] => {
		return [
			{
				id: 'inforBanner',
				header: 'Thông tin Banner',
				minSize: 300,
				cell: ({ row }) => {
					const banner = row.original;
					return (
						<div className='flex items-start gap-4 py-1'>
							<div className='hidden md:flex flex-shrink-0 w-20 h-14 rounded-md border border-border/50 bg-background overflow-hidden items-center justify-center shadow-sm'>
								<img src={banner.path?.length > 0 ? banner.path : no_img} alt='banner' className='object-cover w-full h-full rounded-sm' />
							</div>
							<div className='flex-1 space-y-1'>
								<h6 className='text-sm font-semibold text-foreground/90 flex items-center gap-1'>
									<em className='ni ni-image text-primary'></em>
									{banner.name}
								</h6>
								<p className='text-xs text-muted-foreground'>Kích thước: <b>{banner.width}</b>x<b>{banner.height}</b>px &bull; {banner.extension}</p>
							</div>
						</div>
					);
				}
			},
			{
				id: 'inforAds',
				header: 'Thông tin quảng cáo',
				minSize: 300,
				cell: ({ row }) => {
					const banner = row.original;
					return (
						<div className='space-y-1 py-1'>
							<p className='text-sm font-semibold text-foreground/90'>{banner.title}</p>
							<p className='text-xs text-muted-foreground'>{banner.description}</p>
							<p className='text-xs text-muted-foreground/70 truncate max-w-xs'>{banner.link}</p>
							<p className='text-xs text-muted-foreground/70'>{banner.beginDate} → {banner.endDate}</p>
						</div>
					);
				}
			},
			{
				accessorKey: 'inActive',
				header: 'Trạng thái',
				size: 100,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return !val ? (
						<Badge variant='outline' className='inline-flex items-center rounded-full border-emerald-300/70 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700'>
							Áp dụng
						</Badge>
					) : (
						<Badge variant='outline' className='inline-flex items-center rounded-full border-rose-300/70 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700'>
							Không áp dụng
						</Badge>
					);
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center', className: 'sticky right-0 z-20 bg-background/95' },
				size: 60,
				cell: ({ row }) => (
					<ShadcnButton
						variant='outline'
						size='icon'
						className='h-9 w-9 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary'
						onClick={(e) => {
							e.stopPropagation();
							handleAddEditAction(String(row.original.id));
						}}
						title='Sửa banner'
						aria-label='Sửa banner'
					>
						<Edit className='h-4 w-4' />
					</ShadcnButton>
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => handleAddEditAction()}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<div className='mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs'>
						<span className='font-medium text-foreground/80'>Chú thích trạng thái:</span>
						<span className='inline-flex items-center gap-1 text-emerald-700'><span className='h-2 w-2 rounded-full bg-emerald-500' />Áp dụng</span>
						<span className='inline-flex items-center gap-1 text-rose-700'><span className='h-2 w-2 rounded-full bg-rose-500' />Không áp dụng</span>
					</div>

					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allBanners}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								onRowClick={(row) => handleAddEditAction(String(row.id))}
								enablePagination
								totalRecords={allBanners.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm kiếm banner...`,
									extraActions: (
										<Button
											text='Tạo mới'
											icon='icon ni ni-plus'
											theme='primary'
											className='hidden md:inline-flex'
											onClick={() => handleAddEditAction()}
										/>
									)
								}}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(BannerTable);
